import { groupBy, upperFirst } from "lodash-es";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ActiveFilterBar } from "~/components/Filter/ActiveFilterBar/ActiveFilterBar";
import {
  ActiveFilterButton,
  ActiveFilterButtonProps,
} from "~/components/Filter/ActiveFilterButton/ActiveFilterButton";
import {
  FilterBar,
  FilterBarProps,
} from "~/components/Filter/FilterBar/FilterBar";
import {
  FilterForm,
  FilterFormProps,
} from "~/components/Filter/FilterForm/FilterForm";
import { QuickFilterBar } from "~/components/Filter/QuickFilterBar/QuickFilterBar";
import {
  QuickFilterButton,
  QuickFilterButtonProps,
} from "~/components/Filter/QuickFilterButton/QuickFilterButton";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import {
  AssetFilterType,
  AssetForwardPaginationQueryVariables,
  AssetUrlSegmentInput,
  ScoreRating,
  useGetAssetFilterSuggestionsLazyQuery,
} from "~/operations";
import { FilterMenuOption } from "~/components/Filter/FilterMenu";
import { truncateText } from "~/lib/truncateText";
import { isAssetUrlFilter } from "~/hooks/useAssetUrlStats";
import {
  emptyMenuOption,
  loadingMenuOption,
} from "~/components/Filter/QuickFilterMenu/QuickFilterMenu";

export type InventoryAssetsFilterVariables = Pick<
  NonNullable<AssetForwardPaginationQueryVariables>,
  "platformName" | "queryTerms" | "rating" | "assetUrlFilter"
> & {
  queryTerms?: string[];
  rating?: ScoreRating[];
  platformName?: string[];
  labels?: string[];
  annotations?: string[];
  name?: string[];
  assetUrlFilter?: AssetUrlSegmentInput[];
};

export type UseInventoryAssetsFilterContextParams = {
  scopeMrn: string;
};

export type UseInventoryAssetsFilterContextOutput = {
  filterVariables: InventoryAssetsFilterVariables;
  filterForm: ReactNode;
  filterBar: ReactNode;
  quickFilterBar: ReactNode;
  activeFilterBar: ReactNode;
  activeFilterCount: number;
  resetFilters: () => void;
};

export function useInventoryAssetsFilterContext({
  scopeMrn,
}: UseInventoryAssetsFilterContextParams): UseInventoryAssetsFilterContextOutput {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // search text typed into search bar
  const [searchInput, setSearchInput] = useState("");

  const riskRatingFilterParams = searchParams.getAll("riskRating");
  const platformFilterParams = searchParams.getAll("platform");
  const nameFilterParams = searchParams.getAll("name");
  const labelFilterParams = searchParams.getAll("label");
  const annotationFilterParams = searchParams.getAll("annotation");
  const searchFilterParams = searchParams.getAll("queryTerm");

  const assetUrlParam = searchParams.get("assetUrlFilter");

  const riskRatingFilters = riskRatingFilterParams.every((riskFactorParam) =>
    isScoreRating(riskFactorParam),
  )
    ? riskRatingFilterParams
    : undefined;
  const platformFilters = platformFilterParams.length
    ? platformFilterParams
    : undefined;
  const nameFilters = nameFilterParams.length ? nameFilterParams : undefined;
  const labelFilters = labelFilterParams.length ? labelFilterParams : undefined;
  const annotationFilters = annotationFilterParams.length
    ? annotationFilterParams
    : undefined;
  const searchFilters = searchFilterParams.length
    ? searchFilterParams
    : undefined;

  const assetUrlFilter = getAssetUrlFilter(assetUrlParam);

  const filterVariables: InventoryAssetsFilterVariables = useMemo(() => {
    return {
      ...(riskRatingFilters?.length ? { rating: riskRatingFilters } : {}),
      ...(platformFilters?.length ? { platformName: platformFilters } : {}),
      ...(nameFilters?.length ? { name: nameFilters } : {}),
      ...(labelFilters?.length ? { labels: labelFilters } : {}),
      ...(annotationFilters?.length ? { annotations: annotationFilters } : {}),
      ...(searchFilters?.length ? { queryTerms: searchFilters } : {}),
      ...(assetUrlFilter ? { assetUrlFilter } : {}),
    };
  }, [
    JSON.stringify(riskRatingFilterParams),
    JSON.stringify(platformFilterParams),
    JSON.stringify(nameFilters),
    JSON.stringify(labelFilters),
    JSON.stringify(annotationFilters),
    JSON.stringify(searchFilterParams),
    JSON.stringify(assetUrlFilter),
  ]);

  const riskRatingSuggestions = useInventoryAssetsRiskRatingSuggestions(
    scopeMrn,
    filterVariables,
  );
  const platformSuggestions = useInventoryAssetsPlatformSuggestions(
    scopeMrn,
    filterVariables,
  );
  const nameSuggestions = useInventoryAssetsNameSuggestions(
    scopeMrn,
    filterVariables,
  );

  const addFilter = (param: string, value: string, multiple = false) => {
    const url = new URL(location.href);
    if (multiple) {
      if (!url.searchParams.has(param, value)) {
        url.searchParams.append(param, value);
      }
    } else {
      url.searchParams.set(param, value);
    }
    navigate(url.href.replace(url.origin, ""));
  };

  const removeFilter = (param: string, value?: string) => {
    const url = new URL(location.href);
    url.searchParams.delete(param, value);
    navigate(url.href.replace(url.origin, ""));
  };

  const setFilter = (param: string, value: string) => {
    const url = new URL(location.href);
    url.searchParams.set(param, value);
    navigate(url.href.replace(url.origin, ""));
  };

  const resetFilters = () => {
    const url = new URL(location.href);
    url.searchParams.delete("riskRating");
    url.searchParams.delete("platform");
    url.searchParams.delete("name");
    url.searchParams.delete("label");
    url.searchParams.delete("annotation");
    url.searchParams.delete("queryTerm");
    url.searchParams.delete("assetUrlFilter");
    navigate(url.href.replace(url.origin, ""));
  };

  const activeFilters = [
    riskRatingFilters
      ? riskRatingFilters.map((riskRatingFilter) => {
          return {
            label: "Risk Rating",
            value: formatRiskRatingLabel(riskRatingFilter),
            onClick: () => removeFilter("riskRating", riskRatingFilter),
          };
        })
      : [],
    platformFilters
      ? platformFilters.map((platformFilter) => {
          return {
            label: "Platform",
            value: platformFilter,
            onClick: () => removeFilter("platform", platformFilter),
          };
        })
      : [],
    nameFilters
      ? nameFilters.map((nameFilter) => {
          return {
            label: "Name",
            value: nameFilter,
            onClick: () => removeFilter("name", nameFilter),
          };
        })
      : [],
    labelFilters
      ? labelFilters.map((labelFilter) => {
          return {
            label: "Label",
            value: formatKeyValueLabel(labelFilter),
            onClick: () => removeFilter("label", labelFilter),
          };
        })
      : [],
    annotationFilters
      ? annotationFilters.map((annotationFilter) => {
          return {
            label: "Annotation",
            value: formatKeyValueLabel(annotationFilter),
            onClick: () => removeFilter("annotation", annotationFilter),
          };
        })
      : [],
    searchFilters
      ? searchFilters.map((searchFilter) => {
          return {
            label: "Search",
            value: searchFilter,
            onClick: () => removeFilter("queryTerm", searchFilter),
          };
        })
      : [],
    assetUrlFilter
      ? assetUrlFilter.map(({ key, value }) => {
          return {
            label: "Graph",
            value: `${key}:${value}`,
            onClick: () => {
              const nextFilter = assetUrlFilter.flatMap((f) =>
                f.key === key && f.value === value ? [] : f,
              );
              nextFilter.length
                ? setFilter("assetUrlFilter", JSON.stringify(nextFilter))
                : removeFilter("assetUrlFilter");
            },
          };
        })
      : [],
  ].flatMap((f) => f);

  const [focusedField, setFocusedField] = useState<FilterMenuOption>();

  const fieldOptions = useInventoryAssetsSuggestions(
    focusedField?.field || "",
    searchInput,
    scopeMrn,
    filterVariables,
  );

  useEffect(() => {
    if ("fetchSuggestions" in fieldOptions) {
      fieldOptions.fetchSuggestions();
    }
  }, [focusedField?.field]);

  const filterBar = renderFilterBar({
    fieldOptions: fieldOptions.suggestions,
    focusedField,
    inputValue: searchInput,
    onInputValueChange: (value: string) => {
      setSearchInput(value);
      if ("fetchSuggestions" in fieldOptions) {
        fieldOptions.fetchSuggestions(value);
      }
    },
    onFocusedRemove: () => {
      setFocusedField(undefined);
    },
    onFilterSelect: (filter) => {
      setSearchInput("");
      if (!focusedField) {
        setFocusedField(filter);
      } else {
        setFocusedField(undefined);
        if (filter.param) {
          addFilter(filter.param, filter.value, filter.isMultiple);
        }
      }
    },
    onFilterCreate: (filter) => {
      setFocusedField(undefined);
      setSearchInput("");
      addFilter("queryTerm", filter, true);
    },
  });

  const quickMenuOptions = (param: string, options: FilterMenuOption[]) => {
    return options.map((o) => ({
      ...o,
      onSelect: () => addFilter(param, o.value, o.isMultiple),
    }));
  };

  const quickFilterBar = renderQuickFilterBar([
    {
      label: "Risk Rating",
      activeCount: riskRatingFilters?.length || 0,
      menuOptions: quickMenuOptions(
        "riskRating",
        riskRatingSuggestions.suggestions,
      ),
      onMenuOpen: riskRatingSuggestions.fetchSuggestions,
    },
    // {
    //   label: "Platform",
    //   activeCount: platformFilters?.length || 0,
    //   menuOptions: quickMenuOptions(
    //     "platform",
    //     platformSuggestions.suggestions,
    //   ),
    // },
    // {
    //   label: "Name",
    //   activeCount: nameFilters?.length || 0,
    //   menuOptions: quickMenuOptions("name", nameSuggestions.suggestions),
    // },
  ]);

  const activeFilterBar = renderActiveFilterBar(activeFilters, resetFilters);

  const activeFilterCount = activeFilters.length;

  const filterForm = renderFilterForm({
    filterBar,
    quickFilterBar,
    activeFilterBar,
  });

  return {
    filterVariables,
    filterForm,
    filterBar,
    quickFilterBar,
    activeFilterBar,
    activeFilterCount,
    resetFilters,
  };
}

function renderFilterBar(props: FilterBarProps) {
  return <FilterBar {...props} />;
}

function renderQuickFilterBar(quickFilters: QuickFilterButtonProps[]) {
  return (
    <QuickFilterBar>
      {quickFilters.map((quickFilterProps) => {
        const key = quickFilterProps.label;
        return <QuickFilterButton key={key} {...quickFilterProps} />;
      })}
    </QuickFilterBar>
  );
}

function renderActiveFilterBar(
  activeFilters: ActiveFilterButtonProps[],
  onResetClick: () => void,
) {
  const items = Object.entries(groupBy(activeFilters, "label")).flatMap(
    ([label, values], index) => {
      const buttons = values.map((props) => {
        const key = props.label + props.value;
        return <ActiveFilterButton key={key} {...props} />;
      });
      const separator = (
        <ChevronRightIcon
          key={label + "-separator"}
          sx={{ fontSize: "16px", color: "text.secondary" }}
        />
      );
      return [index === 0 ? [] : separator, ...buttons];
    },
  );
  return (
    items.length > 0 && (
      <ActiveFilterBar onResetClick={onResetClick}>{items}</ActiveFilterBar>
    )
  );
}

function renderFilterForm(props: FilterFormProps) {
  return <FilterForm {...props} />;
}

function useInventoryAssetsSuggestions(
  field: string,
  searchInput: string,
  scopeMrn: string,
  filterVars: InventoryAssetsFilterVariables,
) {
  const categorySuggestions =
    useInventoryAssetsCategorySuggestions(searchInput);
  const riskRatingSuggestions = useInventoryAssetsRiskRatingSuggestions(
    scopeMrn,
    filterVars,
  );
  const platformSuggestions = useInventoryAssetsPlatformSuggestions(
    scopeMrn,
    filterVars,
  );
  const nameSuggestions = useInventoryAssetsNameSuggestions(
    scopeMrn,
    filterVars,
  );
  const labelSuggestions = useInventoryAssetsLabelSuggestions(
    scopeMrn,
    filterVars,
  );
  const annotationSuggestions = useInventoryAssetsAnnotationSuggestions(
    scopeMrn,
    filterVars,
  );
  switch (field) {
    case "riskRating":
      return riskRatingSuggestions;
    case "platform":
      return platformSuggestions;
    case "name":
      return nameSuggestions;
    case "label":
      return labelSuggestions;
    case "annotation":
      return annotationSuggestions;
    default:
      return categorySuggestions;
  }
}

function useInventoryAssetsCategorySuggestions(searchInput: string) {
  const riskRatingFilterGroup: FilterMenuOption = {
    field: "riskRating",
    label: "Risk Rating",
    value: "riskRating",
  };

  const platformFilterGroup: FilterMenuOption = {
    field: "platform",
    label: "Platform",
    value: "platform",
  };

  const nameFilterGroup: FilterMenuOption = {
    field: "name",
    label: "Name",
    value: "name",
  };

  const labelFilterGroup: FilterMenuOption = {
    field: "label",
    label: "Label",
    value: "label",
  };

  const annotationFilterGroup: FilterMenuOption = {
    field: "annotation",
    label: "Annotation",
    value: "annotation",
  };

  const queryFilterGroup: FilterMenuOption = {
    field: "queryTerm",
    label: `Press enter to free text search "${searchInput}"`,
    value: "queryTerm",
    isDisabled: true,
  };

  const suggestions = [
    searchInput.length > 0 ? queryFilterGroup : undefined,
    riskRatingFilterGroup,
    // platformFilterGroup,
    // nameFilterGroup,
    labelFilterGroup,
    annotationFilterGroup,
  ]
    .flatMap((g) => g ?? [])
    .filter((s) =>
      s.label.toLocaleLowerCase().includes(searchInput.toLocaleLowerCase()),
    );
  const loading = false;
  const error = false;

  return {
    suggestions,
    loading,
    error,
  };
}

function useInventoryAssetsRiskRatingSuggestions(
  scopeMrn: string,
  filterVars: InventoryAssetsFilterVariables,
) {
  const [getSuggestions, { data, loading, error, previousData }] =
    useGetAssetFilterSuggestionsLazyQuery();

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        filterType: AssetFilterType.RiskRating,
        searchText,
        scopeMrn,
        filter: {
          platform: filterVars.platformName,
          name: filterVars.name?.[0],
        },
      },
    });
  };

  const param = "riskRating";

  const suggestionsData =
    (data ?? previousData)?.assetFilterSuggestions?.flatMap(
      (suggestion, index) => {
        const value =
          suggestion.value.__typename === "ScoreRatingValue"
            ? suggestion.value.ratingValue
            : "";
        return {
          param,
          field: `${param}.${index}`,
          value: value,
          label: formatRiskRatingLabel(suggestion.label),
          isMultiple: true,
        };
      },
    ) || [];

  const loadingSuggestion = loadingMenuOption(param, `${param}.loading`);
  const emptySuggestion = emptyMenuOption(param, `${param}.empty`);

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}

function useInventoryAssetsPlatformSuggestions(
  scopeMrn: string,
  filterVars: InventoryAssetsFilterVariables,
) {
  const [getSuggestions, { data, loading, error, previousData }] =
    useGetAssetFilterSuggestionsLazyQuery();

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        filterType: AssetFilterType.Platform,
        searchText,
        scopeMrn,
        filter: {
          rating: filterVars.rating,
          name: filterVars.name?.[0],
        },
        limit: 25,
      },
    });
  };

  const param = "platform";

  const suggestionsData =
    (data ?? previousData)?.assetFilterSuggestions?.flatMap(
      (suggestion, index) => {
        const value =
          suggestion.value.__typename === "StringValue"
            ? suggestion.value.stringValue
            : "";
        return {
          param,
          field: `${param}.${index}`,
          value: value,
          label: suggestion.label,
          isMultiple: true,
        };
      },
    ) || [];

  const loadingSuggestion = loadingMenuOption(param, `${param}.loading`);
  const emptySuggestion = emptyMenuOption(param, `${param}.empty`);

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}

function useInventoryAssetsNameSuggestions(
  scopeMrn: string,
  filterVars: InventoryAssetsFilterVariables,
) {
  const [getSuggestions, { data, loading, error, previousData }] =
    useGetAssetFilterSuggestionsLazyQuery();

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        filterType: AssetFilterType.Name,
        searchText,
        scopeMrn,
        filter: {
          rating: filterVars.rating,
          platform: filterVars.platformName,
        },
        limit: 25,
      },
    });
  };

  const param = "name";

  const suggestionsData =
    (data ?? previousData)?.assetFilterSuggestions?.flatMap(
      (suggestion, index) => {
        const value =
          suggestion.value.__typename === "StringValue"
            ? suggestion.value.stringValue
            : "";
        return {
          param,
          field: `${param}.${index}`,
          value: value,
          label: suggestion.label,
          isMultiple: true,
        };
      },
    ) || [];

  const loadingSuggestion = loadingMenuOption(param, `${param}.loading`);
  const emptySuggestion = emptyMenuOption(param, `${param}.empty`);

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}

function useInventoryAssetsLabelSuggestions(
  scopeMrn: string,
  filterVars: InventoryAssetsFilterVariables,
) {
  const [getSuggestions, { data, loading, error, previousData }] =
    useGetAssetFilterSuggestionsLazyQuery();

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        filterType: AssetFilterType.Labels,
        searchText,
        scopeMrn,
        filter: {
          rating: filterVars.rating,
          platform: filterVars.platformName,
          name: filterVars.name?.[0],
        },
        limit: 25,
      },
    });
  };

  const param = "label";

  const suggestionsData =
    (data ?? previousData)?.assetFilterSuggestions?.flatMap(
      (suggestion, index) => {
        const value =
          suggestion.value.__typename === "StringValue"
            ? suggestion.value.stringValue
            : "";
        return {
          param,
          field: `${param}.${index}`,
          value: value,
          label: suggestion.label,
          isMultiple: true,
        };
      },
    ) || [];

  const loadingSuggestion = loadingMenuOption(param, `${param}.loading`);
  const emptySuggestion = emptyMenuOption(param, `${param}.empty`);

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}

function useInventoryAssetsAnnotationSuggestions(
  scopeMrn: string,
  filterVars: InventoryAssetsFilterVariables,
) {
  const [getSuggestions, { data, loading, error, previousData }] =
    useGetAssetFilterSuggestionsLazyQuery();

  const fetchSuggestions = (searchText?: string) => {
    return getSuggestions({
      variables: {
        filterType: AssetFilterType.Annotations,
        searchText,
        scopeMrn,
        filter: {
          rating: filterVars.rating,
          platform: filterVars.platformName,
          name: filterVars.name?.[0],
        },
        limit: 25,
      },
    });
  };

  const param = "annotation";

  const suggestionsData =
    (data ?? previousData)?.assetFilterSuggestions?.flatMap(
      (suggestion, index) => {
        const value =
          suggestion.value.__typename === "StringValue"
            ? suggestion.value.stringValue
            : "";
        return {
          param,
          field: `${param}.${index}`,
          value: value,
          label: suggestion.label,
          isMultiple: true,
        };
      },
    ) || [];

  const loadingSuggestion = loadingMenuOption(param, `${param}.loading`);
  const emptySuggestion = emptyMenuOption(param, `${param}.empty`);

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}

function isScoreRating(value: any): value is ScoreRating {
  return Object.values(ScoreRating).includes(value);
}

function formatRiskRatingLabel(label: string) {
  return upperFirst(label.toLocaleLowerCase());
}

// "{'distro-id': 'ubuntu'}" => "distro-id:ubuntu"
function formatKeyValueLabel(label: string) {
  try {
    const [keyValue]: [string, string][] = Object.entries(JSON.parse(label));
    const [key, value] = keyValue;
    return [truncateText(key, 20), truncateText(value, 20)].join(":");
  } catch (error) {
    return label;
  }
}

function getAssetUrlFilter(param?: string | null) {
  try {
    if (!param) return undefined;
    const assetUrlFilter = JSON.parse(param);
    if (isAssetUrlFilter(assetUrlFilter)) {
      return assetUrlFilter;
    }
  } catch (error) {
    return undefined;
  }
}
